// Static curated emoji catalog used by the composer's emoji overlay.
//
// We intentionally avoid bundling a third-party Unicode dataset (the
// CLDR short-name files are CC-BY licensed but heavy, and the
// emoji-keyword corpora vary in license). Instead, this catalog is a
// hand-curated short-name + keyword list covering the most-used
// ~160 emoji, grouped into the eight standard categories so the
// picker can present them in a stable order. The entries here use
// short names + keywords written from scratch (not copy-pasted from
// any copyrighted source), and the glyphs themselves are bare
// Unicode code points.
//
// Filtering is host-driven: `ChatRoomComposerRich` keeps an
// `emojiQuery` string and filters the array on each keystroke with a
// case-insensitive substring match against `name + keywords`. When
// the query is empty the full list (grouped) is shown.

export type EmojiGroup =
  | 'smileys'
  | 'people'
  | 'animals'
  | 'food'
  | 'activities'
  | 'travel'
  | 'objects'
  | 'symbols'
  | 'flags';

export interface EmojiCatalogEntry {
  readonly code: string;
  readonly name: string;
  readonly group: EmojiGroup;
  readonly keywords: readonly string[];
}

// Curated entries. Order within each group roughly mirrors a
// "frequently used" ranking — common ones first.
export const EMOJI_CATALOG: readonly EmojiCatalogEntry[] = [
  // Smileys -----------------------------------------------------------
  { code: '\u{1F600}', name: 'grinning face', group: 'smileys', keywords: ['happy', 'smile', 'grin'] },
  { code: '\u{1F603}', name: 'big smile', group: 'smileys', keywords: ['happy', 'smile', 'open'] },
  { code: '\u{1F604}', name: 'smiling eyes', group: 'smileys', keywords: ['happy', 'smile', 'eyes'] },
  { code: '\u{1F601}', name: 'beaming grin', group: 'smileys', keywords: ['happy', 'grin', 'teeth'] },
  { code: '\u{1F606}', name: 'laughing', group: 'smileys', keywords: ['laugh', 'lol', 'haha'] },
  { code: '\u{1F605}', name: 'sweat smile', group: 'smileys', keywords: ['nervous', 'phew', 'relief'] },
  { code: '\u{1F923}', name: 'rofl', group: 'smileys', keywords: ['lol', 'laugh', 'rolling', 'floor'] },
  { code: '\u{1F602}', name: 'tears of joy', group: 'smileys', keywords: ['cry', 'laugh', 'lol', 'haha'] },
  { code: '\u{1F642}', name: 'slight smile', group: 'smileys', keywords: ['smile', 'small'] },
  { code: '\u{1F643}', name: 'upside down', group: 'smileys', keywords: ['silly', 'flipped'] },
  { code: '\u{1F609}', name: 'wink', group: 'smileys', keywords: ['flirt', 'cheeky'] },
  { code: '\u{1F60A}', name: 'blushing smile', group: 'smileys', keywords: ['happy', 'blush', 'shy'] },
  { code: '\u{1F607}', name: 'angel', group: 'smileys', keywords: ['halo', 'innocent', 'good'] },
  { code: '\u{1F970}', name: 'smiling with hearts', group: 'smileys', keywords: ['love', 'adore', 'crush'] },
  { code: '\u{1F60D}', name: 'heart eyes', group: 'smileys', keywords: ['love', 'crush', 'adore'] },
  { code: '\u{1F618}', name: 'kiss', group: 'smileys', keywords: ['love', 'kiss', 'mwah'] },
  { code: '\u{1F617}', name: 'kissing face', group: 'smileys', keywords: ['kiss', 'pucker'] },
  { code: '\u{1F619}', name: 'kissing eyes', group: 'smileys', keywords: ['kiss', 'eyes'] },
  { code: '\u{1F61A}', name: 'kissing closed', group: 'smileys', keywords: ['kiss', 'shy'] },
  { code: '\u{1F60B}', name: 'yum', group: 'smileys', keywords: ['tongue', 'delicious', 'tasty'] },
  { code: '\u{1F61B}', name: 'tongue out', group: 'smileys', keywords: ['silly', 'tongue', 'playful'] },
  { code: '\u{1F61D}', name: 'tongue eyes shut', group: 'smileys', keywords: ['silly', 'laugh', 'tongue'] },
  { code: '\u{1F61C}', name: 'tongue wink', group: 'smileys', keywords: ['silly', 'wink', 'joke'] },
  { code: '\u{1F92A}', name: 'zany', group: 'smileys', keywords: ['crazy', 'silly', 'wild'] },
  { code: '\u{1F914}', name: 'thinking', group: 'smileys', keywords: ['thinking', 'hmm', 'ponder'] },
  { code: '\u{1F910}', name: 'zipped mouth', group: 'smileys', keywords: ['quiet', 'secret', 'silent'] },
  { code: '\u{1F928}', name: 'raised eyebrow', group: 'smileys', keywords: ['skeptical', 'suspicious'] },
  { code: '\u{1F610}', name: 'neutral', group: 'smileys', keywords: ['meh', 'blank'] },
  { code: '\u{1F611}', name: 'expressionless', group: 'smileys', keywords: ['meh', 'blank', 'deadpan'] },
  { code: '\u{1F636}', name: 'no mouth', group: 'smileys', keywords: ['speechless', 'quiet'] },
  { code: '\u{1F60F}', name: 'smirk', group: 'smileys', keywords: ['smug', 'sly', 'grin'] },
  { code: '\u{1F612}', name: 'unamused', group: 'smileys', keywords: ['meh', 'side eye', 'unimpressed'] },
  { code: '\u{1F644}', name: 'eye roll', group: 'smileys', keywords: ['annoyed', 'ugh', 'whatever'] },
  { code: '\u{1F62C}', name: 'grimacing', group: 'smileys', keywords: ['awkward', 'eek', 'teeth'] },
  { code: '\u{1F925}', name: 'liar', group: 'smileys', keywords: ['lying', 'pinocchio', 'nose'] },
  { code: '\u{1F60C}', name: 'relieved', group: 'smileys', keywords: ['phew', 'relaxed', 'calm'] },
  { code: '\u{1F614}', name: 'pensive', group: 'smileys', keywords: ['sad', 'sigh', 'thoughtful'] },
  { code: '\u{1F62A}', name: 'sleepy', group: 'smileys', keywords: ['tired', 'sleep'] },
  { code: '\u{1F924}', name: 'drooling', group: 'smileys', keywords: ['yum', 'hungry', 'drool'] },
  { code: '\u{1F634}', name: 'sleeping', group: 'smileys', keywords: ['sleep', 'zzz'] },
  { code: '\u{1F637}', name: 'masked', group: 'smileys', keywords: ['mask', 'sick', 'covid'] },
  { code: '\u{1F912}', name: 'thermometer', group: 'smileys', keywords: ['sick', 'fever'] },
  { code: '\u{1F915}', name: 'bandaged head', group: 'smileys', keywords: ['injured', 'hurt'] },
  { code: '\u{1F922}', name: 'queasy', group: 'smileys', keywords: ['sick', 'nausea'] },
  { code: '\u{1F92E}', name: 'vomit', group: 'smileys', keywords: ['sick', 'puke'] },
  { code: '\u{1F927}', name: 'sneeze', group: 'smileys', keywords: ['sick', 'achoo'] },
  { code: '\u{1F975}', name: 'hot', group: 'smileys', keywords: ['hot', 'sweat', 'sun'] },
  { code: '\u{1F976}', name: 'cold', group: 'smileys', keywords: ['cold', 'freezing'] },
  { code: '\u{1F635}', name: 'dizzy', group: 'smileys', keywords: ['dizzy', 'shock'] },
  { code: '\u{1F92F}', name: 'mind blown', group: 'smileys', keywords: ['shock', 'explode', 'wow'] },
  { code: '\u{1F920}', name: 'cowboy', group: 'smileys', keywords: ['cowboy', 'yeehaw', 'hat'] },
  { code: '\u{1F973}', name: 'party', group: 'smileys', keywords: ['party', 'birthday', 'celebrate'] },
  { code: '\u{1F60E}', name: 'sunglasses', group: 'smileys', keywords: ['cool', 'shades'] },
  { code: '\u{1F913}', name: 'nerd', group: 'smileys', keywords: ['glasses', 'geek', 'smart'] },
  { code: '\u{1F9D0}', name: 'monocle', group: 'smileys', keywords: ['inspect', 'examine'] },
  { code: '\u{1F615}', name: 'confused', group: 'smileys', keywords: ['unsure', 'hmm'] },
  { code: '\u{1F61F}', name: 'worried', group: 'smileys', keywords: ['concern', 'nervous'] },
  { code: '\u{1F641}', name: 'slight frown', group: 'smileys', keywords: ['sad', 'frown'] },
  { code: '\u{2639}\u{FE0F}', name: 'frowning', group: 'smileys', keywords: ['sad', 'frown', 'unhappy'] },
  { code: '\u{1F62E}', name: 'open mouth', group: 'smileys', keywords: ['surprise', 'shock'] },
  { code: '\u{1F62F}', name: 'hushed', group: 'smileys', keywords: ['quiet', 'shh'] },
  { code: '\u{1F632}', name: 'astonished', group: 'smileys', keywords: ['shock', 'wow'] },
  { code: '\u{1F633}', name: 'flushed', group: 'smileys', keywords: ['embarrassed', 'blush'] },
  { code: '\u{1F97A}', name: 'pleading', group: 'smileys', keywords: ['please', 'puppy', 'sad'] },
  { code: '\u{1F626}', name: 'frowning open', group: 'smileys', keywords: ['sad', 'frustrated'] },
  { code: '\u{1F627}', name: 'anguished', group: 'smileys', keywords: ['sad', 'distressed'] },
  { code: '\u{1F628}', name: 'fearful', group: 'smileys', keywords: ['scared', 'shock'] },
  { code: '\u{1F630}', name: 'anxious', group: 'smileys', keywords: ['worried', 'sweat'] },
  { code: '\u{1F625}', name: 'sad sweat', group: 'smileys', keywords: ['sad', 'cry'] },
  { code: '\u{1F622}', name: 'crying', group: 'smileys', keywords: ['sad', 'tear', 'cry'] },
  { code: '\u{1F62D}', name: 'loudly crying', group: 'smileys', keywords: ['sob', 'cry', 'sad'] },
  { code: '\u{1F631}', name: 'screaming', group: 'smileys', keywords: ['scream', 'shock', 'fear'] },
  { code: '\u{1F616}', name: 'confounded', group: 'smileys', keywords: ['frustrated', 'sad'] },
  { code: '\u{1F623}', name: 'persevering', group: 'smileys', keywords: ['struggle', 'effort'] },
  { code: '\u{1F61E}', name: 'disappointed', group: 'smileys', keywords: ['sad', 'down'] },
  { code: '\u{1F613}', name: 'cold sweat', group: 'smileys', keywords: ['stress', 'tired'] },
  { code: '\u{1F629}', name: 'weary', group: 'smileys', keywords: ['tired', 'exhausted'] },
  { code: '\u{1F62B}', name: 'tired', group: 'smileys', keywords: ['exhausted', 'overwhelmed'] },
  { code: '\u{1F971}', name: 'yawning', group: 'smileys', keywords: ['tired', 'bored'] },
  { code: '\u{1F624}', name: 'angry', group: 'smileys', keywords: ['mad', 'steam', 'frustrated'] },
  { code: '\u{1F621}', name: 'rage', group: 'smileys', keywords: ['mad', 'red', 'angry'] },
  { code: '\u{1F620}', name: 'pouting', group: 'smileys', keywords: ['angry', 'mad'] },
  { code: '\u{1F608}', name: 'devil', group: 'smileys', keywords: ['evil', 'horns'] },
  { code: '\u{1F47F}', name: 'imp', group: 'smileys', keywords: ['devil', 'angry'] },
  { code: '\u{1F480}', name: 'skull', group: 'smileys', keywords: ['dead', 'lol', 'skeleton'] },
  { code: '\u{1F47B}', name: 'ghost', group: 'smileys', keywords: ['spooky', 'halloween'] },
  { code: '\u{1F4A9}', name: 'poop', group: 'smileys', keywords: ['poo', 'crap', 'funny'] },
  { code: '\u{1F921}', name: 'clown', group: 'smileys', keywords: ['joke', 'silly'] },
  { code: '\u{1F916}', name: 'robot', group: 'smileys', keywords: ['bot', 'ai', 'tech'] },
  // People / hands ----------------------------------------------------
  { code: '\u{1F44B}', name: 'wave', group: 'people', keywords: ['hi', 'hello', 'bye'] },
  { code: '\u{1F44D}', name: 'thumbs up', group: 'people', keywords: ['like', 'yes', 'good'] },
  { code: '\u{1F44E}', name: 'thumbs down', group: 'people', keywords: ['dislike', 'no', 'bad'] },
  { code: '\u{1F44F}', name: 'clap', group: 'people', keywords: ['applause', 'good job'] },
  { code: '\u{1F64C}', name: 'raised hands', group: 'people', keywords: ['praise', 'celebrate'] },
  { code: '\u{1F64F}', name: 'folded hands', group: 'people', keywords: ['pray', 'thanks', 'please'] },
  { code: '\u{1F91D}', name: 'handshake', group: 'people', keywords: ['deal', 'agree'] },
  { code: '\u{270C}\u{FE0F}', name: 'peace', group: 'people', keywords: ['victory', 'two'] },
  { code: '\u{1F91E}', name: 'crossed fingers', group: 'people', keywords: ['luck', 'hope'] },
  { code: '\u{1F918}', name: 'rock on', group: 'people', keywords: ['metal', 'cool'] },
  { code: '\u{1F44C}', name: 'ok hand', group: 'people', keywords: ['perfect', 'good'] },
  { code: '\u{1F90F}', name: 'pinching hand', group: 'people', keywords: ['small', 'tiny'] },
  { code: '\u{270A}', name: 'fist', group: 'people', keywords: ['power', 'punch'] },
  { code: '\u{1F44A}', name: 'punch', group: 'people', keywords: ['fight', 'hit'] },
  { code: '\u{1F4AA}', name: 'flex', group: 'people', keywords: ['strong', 'muscle', 'gains'] },
  { code: '\u{270B}', name: 'open hand', group: 'people', keywords: ['stop', 'high five'] },
  { code: '\u{1F590}\u{FE0F}', name: 'splayed hand', group: 'people', keywords: ['hand', 'five'] },
  { code: '\u{1F919}', name: 'call me', group: 'people', keywords: ['shaka', 'phone'] },
  { code: '\u{1F446}', name: 'point up', group: 'people', keywords: ['up', 'attention'] },
  { code: '\u{1F447}', name: 'point down', group: 'people', keywords: ['down'] },
  { code: '\u{1F448}', name: 'point left', group: 'people', keywords: ['left'] },
  { code: '\u{1F449}', name: 'point right', group: 'people', keywords: ['right'] },
  { code: '\u{1F4AF}', name: 'one hundred', group: 'people', keywords: ['perfect', '100', 'agree'] },
  // Animals -----------------------------------------------------------
  { code: '\u{1F436}', name: 'dog', group: 'animals', keywords: ['puppy', 'pet'] },
  { code: '\u{1F431}', name: 'cat', group: 'animals', keywords: ['kitty', 'pet'] },
  { code: '\u{1F42D}', name: 'mouse', group: 'animals', keywords: ['rodent'] },
  { code: '\u{1F430}', name: 'rabbit', group: 'animals', keywords: ['bunny'] },
  { code: '\u{1F98A}', name: 'fox', group: 'animals', keywords: ['fox'] },
  { code: '\u{1F43B}', name: 'bear', group: 'animals', keywords: ['bear'] },
  { code: '\u{1F43C}', name: 'panda', group: 'animals', keywords: ['bear', 'china'] },
  { code: '\u{1F428}', name: 'koala', group: 'animals', keywords: ['australia'] },
  { code: '\u{1F981}', name: 'lion', group: 'animals', keywords: ['king', 'roar'] },
  { code: '\u{1F42E}', name: 'cow', group: 'animals', keywords: ['cattle', 'moo'] },
  { code: '\u{1F437}', name: 'pig', group: 'animals', keywords: ['oink'] },
  { code: '\u{1F435}', name: 'monkey', group: 'animals', keywords: ['ape'] },
  { code: '\u{1F40D}', name: 'snake', group: 'animals', keywords: ['serpent'] },
  { code: '\u{1F40C}', name: 'snail', group: 'animals', keywords: ['slow'] },
  { code: '\u{1F41D}', name: 'bee', group: 'animals', keywords: ['honey', 'buzz'] },
  { code: '\u{1F41E}', name: 'ladybug', group: 'animals', keywords: ['bug', 'insect'] },
  { code: '\u{1F98B}', name: 'butterfly', group: 'animals', keywords: ['flutter'] },
  { code: '\u{1F420}', name: 'fish', group: 'animals', keywords: ['tropical'] },
  { code: '\u{1F419}', name: 'octopus', group: 'animals', keywords: ['sea'] },
  { code: '\u{1F40B}', name: 'whale', group: 'animals', keywords: ['ocean'] },
  // Food --------------------------------------------------------------
  { code: '\u{1F34E}', name: 'apple', group: 'food', keywords: ['fruit', 'red'] },
  { code: '\u{1F34C}', name: 'banana', group: 'food', keywords: ['fruit', 'yellow'] },
  { code: '\u{1F347}', name: 'grapes', group: 'food', keywords: ['fruit', 'wine'] },
  { code: '\u{1F353}', name: 'strawberry', group: 'food', keywords: ['fruit', 'berry'] },
  { code: '\u{1F349}', name: 'watermelon', group: 'food', keywords: ['fruit', 'summer'] },
  { code: '\u{1F34A}', name: 'orange', group: 'food', keywords: ['fruit', 'citrus'] },
  { code: '\u{1F34B}', name: 'lemon', group: 'food', keywords: ['fruit', 'citrus', 'sour'] },
  { code: '\u{1F34D}', name: 'pineapple', group: 'food', keywords: ['fruit', 'tropical'] },
  { code: '\u{1F951}', name: 'avocado', group: 'food', keywords: ['toast', 'green'] },
  { code: '\u{1F345}', name: 'tomato', group: 'food', keywords: ['fruit', 'red'] },
  { code: '\u{1F955}', name: 'carrot', group: 'food', keywords: ['vegetable'] },
  { code: '\u{1F33D}', name: 'corn', group: 'food', keywords: ['vegetable', 'maize'] },
  { code: '\u{1F354}', name: 'burger', group: 'food', keywords: ['food', 'fast food'] },
  { code: '\u{1F35F}', name: 'fries', group: 'food', keywords: ['food', 'potato'] },
  { code: '\u{1F355}', name: 'pizza', group: 'food', keywords: ['food', 'slice'] },
  { code: '\u{1F32D}', name: 'hot dog', group: 'food', keywords: ['food', 'sausage'] },
  { code: '\u{1F32E}', name: 'taco', group: 'food', keywords: ['food', 'mexican'] },
  { code: '\u{1F32F}', name: 'burrito', group: 'food', keywords: ['food', 'mexican'] },
  { code: '\u{1F35E}', name: 'bread', group: 'food', keywords: ['food', 'loaf'] },
  { code: '\u{1F95E}', name: 'pancakes', group: 'food', keywords: ['breakfast'] },
  { code: '\u{1F35C}', name: 'ramen', group: 'food', keywords: ['noodles', 'soup'] },
  { code: '\u{1F363}', name: 'sushi', group: 'food', keywords: ['japanese', 'fish'] },
  { code: '\u{1F366}', name: 'ice cream', group: 'food', keywords: ['dessert', 'cold'] },
  { code: '\u{1F368}', name: 'ice cream cone', group: 'food', keywords: ['dessert'] },
  { code: '\u{1F370}', name: 'cake', group: 'food', keywords: ['dessert', 'birthday'] },
  { code: '\u{1F36A}', name: 'cookie', group: 'food', keywords: ['dessert'] },
  { code: '\u{1F36B}', name: 'chocolate', group: 'food', keywords: ['candy', 'dessert'] },
  { code: '\u{1F369}', name: 'donut', group: 'food', keywords: ['dessert', 'sweet'] },
  { code: '\u{2615}', name: 'coffee', group: 'food', keywords: ['drink', 'caffeine'] },
  { code: '\u{1F375}', name: 'tea', group: 'food', keywords: ['drink', 'hot'] },
  { code: '\u{1F37A}', name: 'beer', group: 'food', keywords: ['drink', 'alcohol'] },
  { code: '\u{1F377}', name: 'wine', group: 'food', keywords: ['drink', 'alcohol'] },
  { code: '\u{1F379}', name: 'cocktail', group: 'food', keywords: ['drink', 'alcohol'] },
  { code: '\u{1F942}', name: 'cheers', group: 'food', keywords: ['toast', 'drink', 'celebrate'] },
  // Activities --------------------------------------------------------
  { code: '\u{26BD}', name: 'soccer', group: 'activities', keywords: ['sport', 'football'] },
  { code: '\u{1F3C0}', name: 'basketball', group: 'activities', keywords: ['sport'] },
  { code: '\u{1F3C8}', name: 'football', group: 'activities', keywords: ['sport', 'american'] },
  { code: '\u{26BE}', name: 'baseball', group: 'activities', keywords: ['sport'] },
  { code: '\u{1F3BE}', name: 'tennis', group: 'activities', keywords: ['sport'] },
  { code: '\u{1F3B1}', name: 'pool', group: 'activities', keywords: ['8ball', 'billiards'] },
  { code: '\u{1F3AE}', name: 'video game', group: 'activities', keywords: ['gaming', 'controller'] },
  { code: '\u{1F3B2}', name: 'dice', group: 'activities', keywords: ['game', 'luck'] },
  { code: '\u{1F3B8}', name: 'guitar', group: 'activities', keywords: ['music', 'instrument'] },
  { code: '\u{1F3B5}', name: 'music', group: 'activities', keywords: ['song', 'note'] },
  { code: '\u{1F3A8}', name: 'art', group: 'activities', keywords: ['paint', 'palette'] },
  { code: '\u{1F389}', name: 'party popper', group: 'activities', keywords: ['celebrate', 'birthday'] },
  { code: '\u{1F38A}', name: 'confetti', group: 'activities', keywords: ['celebrate', 'party'] },
  { code: '\u{1F388}', name: 'balloon', group: 'activities', keywords: ['party', 'birthday'] },
  { code: '\u{1F381}', name: 'gift', group: 'activities', keywords: ['present', 'birthday'] },
  // Travel ------------------------------------------------------------
  { code: '\u{1F697}', name: 'car', group: 'travel', keywords: ['drive', 'vehicle'] },
  { code: '\u{1F695}', name: 'taxi', group: 'travel', keywords: ['cab'] },
  { code: '\u{1F699}', name: 'suv', group: 'travel', keywords: ['drive'] },
  { code: '\u{1F68C}', name: 'bus', group: 'travel', keywords: ['transit'] },
  { code: '\u{1F683}', name: 'train', group: 'travel', keywords: ['transit'] },
  { code: '\u{1F6F4}', name: 'scooter', group: 'travel', keywords: ['kick'] },
  { code: '\u{1F6B2}', name: 'bike', group: 'travel', keywords: ['bicycle'] },
  { code: '\u{2708}\u{FE0F}', name: 'airplane', group: 'travel', keywords: ['flight', 'travel'] },
  { code: '\u{1F680}', name: 'rocket', group: 'travel', keywords: ['launch', 'space'] },
  { code: '\u{1F6F8}', name: 'ufo', group: 'travel', keywords: ['alien', 'space'] },
  { code: '\u{1F30D}', name: 'earth', group: 'travel', keywords: ['world', 'globe'] },
  { code: '\u{1F306}', name: 'cityscape', group: 'travel', keywords: ['city', 'sunset'] },
  { code: '\u{1F3D6}\u{FE0F}', name: 'beach', group: 'travel', keywords: ['vacation', 'sand'] },
  { code: '\u{26F0}\u{FE0F}', name: 'mountain', group: 'travel', keywords: ['hike'] },
  { code: '\u{1F3D5}\u{FE0F}', name: 'camping', group: 'travel', keywords: ['tent', 'outdoor'] },
  // Objects -----------------------------------------------------------
  { code: '\u{1F4F1}', name: 'phone', group: 'objects', keywords: ['mobile', 'cell'] },
  { code: '\u{1F4BB}', name: 'laptop', group: 'objects', keywords: ['computer'] },
  { code: '\u{1F5A5}\u{FE0F}', name: 'desktop', group: 'objects', keywords: ['computer'] },
  { code: '\u{1F4F7}', name: 'camera', group: 'objects', keywords: ['photo'] },
  { code: '\u{1F4F8}', name: 'camera flash', group: 'objects', keywords: ['photo', 'flash'] },
  { code: '\u{1F4FD}\u{FE0F}', name: 'film projector', group: 'objects', keywords: ['movie', 'video'] },
  { code: '\u{1F39E}\u{FE0F}', name: 'film', group: 'objects', keywords: ['movie', 'cinema'] },
  { code: '\u{1F4DA}', name: 'books', group: 'objects', keywords: ['read', 'library'] },
  { code: '\u{1F4D6}', name: 'open book', group: 'objects', keywords: ['read'] },
  { code: '\u{1F4DD}', name: 'memo', group: 'objects', keywords: ['note', 'write'] },
  { code: '\u{270F}\u{FE0F}', name: 'pencil', group: 'objects', keywords: ['write', 'edit'] },
  { code: '\u{1F4CC}', name: 'pin', group: 'objects', keywords: ['pushpin', 'attach'] },
  { code: '\u{1F4CE}', name: 'paperclip', group: 'objects', keywords: ['attach'] },
  { code: '\u{1F511}', name: 'key', group: 'objects', keywords: ['lock', 'unlock'] },
  { code: '\u{1F4A1}', name: 'idea', group: 'objects', keywords: ['bulb', 'light', 'thought'] },
  { code: '\u{1F50D}', name: 'magnifier', group: 'objects', keywords: ['search', 'find'] },
  { code: '\u{1F381}', name: 'present', group: 'objects', keywords: ['gift', 'birthday'] },
  { code: '\u{1F4B0}', name: 'money', group: 'objects', keywords: ['cash', 'rich'] },
  { code: '\u{1F4B8}', name: 'flying money', group: 'objects', keywords: ['spend', 'cash'] },
  { code: '\u{1F525}', name: 'fire', group: 'objects', keywords: ['lit', 'flame', 'hot'] },
  { code: '\u{1F4A7}', name: 'droplet', group: 'objects', keywords: ['water'] },
  { code: '\u{1F4A4}', name: 'sleep', group: 'objects', keywords: ['zzz', 'tired'] },
  { code: '\u{1F4AC}', name: 'speech bubble', group: 'objects', keywords: ['talk', 'chat'] },
  { code: '\u{1F4AD}', name: 'thought bubble', group: 'objects', keywords: ['think', 'dream'] },
  // Symbols -----------------------------------------------------------
  { code: '\u{2764}\u{FE0F}', name: 'red heart', group: 'symbols', keywords: ['love', 'heart'] },
  { code: '\u{1F9E1}', name: 'orange heart', group: 'symbols', keywords: ['heart'] },
  { code: '\u{1F49B}', name: 'yellow heart', group: 'symbols', keywords: ['heart'] },
  { code: '\u{1F49A}', name: 'green heart', group: 'symbols', keywords: ['heart'] },
  { code: '\u{1F499}', name: 'blue heart', group: 'symbols', keywords: ['heart'] },
  { code: '\u{1F49C}', name: 'purple heart', group: 'symbols', keywords: ['heart'] },
  { code: '\u{1F90D}', name: 'white heart', group: 'symbols', keywords: ['heart'] },
  { code: '\u{1F5A4}', name: 'black heart', group: 'symbols', keywords: ['heart'] },
  { code: '\u{1F90E}', name: 'brown heart', group: 'symbols', keywords: ['heart'] },
  { code: '\u{1F494}', name: 'broken heart', group: 'symbols', keywords: ['sad', 'breakup'] },
  { code: '\u{2763}\u{FE0F}', name: 'heart exclamation', group: 'symbols', keywords: ['love'] },
  { code: '\u{1F495}', name: 'two hearts', group: 'symbols', keywords: ['love'] },
  { code: '\u{1F496}', name: 'sparkling heart', group: 'symbols', keywords: ['love'] },
  { code: '\u{1F49E}', name: 'revolving hearts', group: 'symbols', keywords: ['love'] },
  { code: '\u{1F498}', name: 'cupid', group: 'symbols', keywords: ['love', 'arrow'] },
  { code: '\u{2728}', name: 'sparkles', group: 'symbols', keywords: ['shine', 'magic', 'new'] },
  { code: '\u{2B50}', name: 'star', group: 'symbols', keywords: ['favorite', 'rate'] },
  { code: '\u{1F31F}', name: 'glowing star', group: 'symbols', keywords: ['shine', 'special'] },
  { code: '\u{2705}', name: 'check mark', group: 'symbols', keywords: ['done', 'yes', 'ok'] },
  { code: '\u{274C}', name: 'cross', group: 'symbols', keywords: ['no', 'wrong', 'cancel'] },
  { code: '\u{2757}', name: 'exclamation', group: 'symbols', keywords: ['warning', 'alert'] },
  { code: '\u{2753}', name: 'question', group: 'symbols', keywords: ['huh', 'unsure'] },
  { code: '\u{26A0}\u{FE0F}', name: 'warning', group: 'symbols', keywords: ['caution', 'danger'] },
  { code: '\u{1F6AB}', name: 'no entry', group: 'symbols', keywords: ['forbidden', 'stop'] },
  // Flags -------------------------------------------------------------
  { code: '\u{1F3C1}', name: 'checkered flag', group: 'flags', keywords: ['race', 'finish'] },
  { code: '\u{1F3F3}\u{FE0F}\u{200D}\u{1F308}', name: 'rainbow flag', group: 'flags', keywords: ['pride', 'lgbt'] },
  { code: '\u{1F3F4}\u{200D}\u{2620}\u{FE0F}', name: 'pirate flag', group: 'flags', keywords: ['skull', 'jolly roger'] },
] as const;

/**
 * Returns a filtered slice of the catalog matching `query`. Empty
 * query returns the full list. Match is case-insensitive substring
 * against `name` first, then `keywords`. Stable order — entries keep
 * their catalog rank.
 */
export function filterEmojiCatalog(
  catalog: readonly EmojiCatalogEntry[],
  query: string,
): readonly EmojiCatalogEntry[] {
  const q = query.trim().toLowerCase();
  if (q.length === 0) return catalog;
  const out: EmojiCatalogEntry[] = [];
  for (const entry of catalog) {
    if (entry.name.toLowerCase().includes(q)) {
      out.push(entry);
      continue;
    }
    for (const kw of entry.keywords) {
      if (kw.toLowerCase().includes(q)) {
        out.push(entry);
        break;
      }
    }
  }
  return out;
}
